/**
 * 一斉配信の詳細 `/campaigns/$campaignId`。
 *
 * **この画面の存在理由は「止めたのに送られた」の一次確認**。
 *
 * 個別キャンペーンの緊急停止（`POST /ops/campaigns/:id/halt`）は一覧から
 * 撃てたが、その後にバッチが本当に止まったかを見る画面が無かった。
 * `campaigns.status` を `halted` にするだけでは止まらず、積み終わった
 * `campaign_batches` が `pending` のまま残っていればノードからの払い出し
 * （`POST /agent/bulk/lease`）でそのまま注入される。
 * 事故対応中に `pending` が残っていないかを確かめる手段が DB 直の SELECT
 * しか無く、`ops.bulk.halt` を持つ support ロールには手も足も出なかった。
 *
 * **本文は出ない**（不変条件 9）。件名までは運用判断に要るので API が返すが、
 * ブロック JSON / HTML は CP の DB に無い。
 */
import * as React from 'react';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OctagonX } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  KeyValue,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
  DataTable,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FormDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { DetailSkeleton, PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { OpsCampaignDetail } from '../types/api.js';

const routeApi = getRouteApi(RID.campaignDetail);

/** 停止できる状態。一覧（campaigns.tsx）と同じ集合を使う。 */
const HALTABLE = new Set([
  'scheduled',
  'sending',
  'paused',
  'queued',
  'awaiting_release',
  'validated',
  'test_sent',
]);

/**
 * **まだ宛先が送られうるバッチの状態**（`campaign_batch_state` の一部）。
 *
 * 語彙は `pending / leased / injected / failed / canceled` の 5 つ。
 * `pending` は未払い出し、`leased` はノードが持っていってまだ報告が無いもので、
 * どちらかが残っている限り「止まった」とは言えない。
 * `injected` は既にノードの MTA へ渡した後なので、ここには含めない
 * （残っていても止める余地がもう無い）。
 */
const LIVE_BATCH_STATES = ['pending', 'leased'];

interface BatchRow {
  state: string;
  count: number;
}

/* ------------------------------------------------------------- 効果測定 */

/**
 * `GET /ops/campaigns/:id` が返すクリック / 返信の内訳。
 *
 * **`types/api.ts` ではなくここに置いてある。** この 2 つのブロックを使うのは
 * この画面だけで、共有の型ファイルは並行して別の作業が入っている。
 * 画面が増えたら `OpsCampaignDetail` 側へ寄せること。
 */
interface OpsCampaignClickLink {
  linkIndex: number | null;
  url: string | null;
  clicks: number;
  uniqueClicks: number;
  /** 延べに占める割合（0..1）。総クリックが 0 なら null。 */
  share: number | null;
}

interface OpsCampaignMeasure {
  clicks: {
    /** リストの `track_clicks`。**false のときの 0 は「測っていない」**。 */
    tracked: boolean;
    /** `events`（生ログ）か `stats`（保持期間後の焼き付け値）か。 */
    source: 'events' | 'stats';
    retentionDays: number;
    total: number | null;
    unique: number | null;
    rate: number | null;
    uniqueRate: number | null;
    denominator: number;
    denominatorBasis: string;
    firstAt: string | null;
    lastAt: string | null;
    links: OpsCampaignClickLink[];
    linksTruncated: boolean;
    timelineStartsAt: string | null;
    timelineBucketSeconds: number;
    timeline: Array<{ offsetSeconds: number; startsAt: string; clicks: number; uniqueClicks: number }>;
  };
  replies: {
    /** 取り込み経路がこのビルドに在るか。**false は「0 件」ではない**。 */
    available: boolean;
    total: number | null;
    unique: number | null;
    rate: number | null;
    uniqueRate: number | null;
    firstAt: string | null;
    lastAt: string | null;
  };
}

type OpsCampaignDetailView = OpsCampaignDetail & OpsCampaignMeasure;

/** 率（0..1）を % 表記へ。**測っていない（null）は「—」**で、0.0% と書かない。 */
export function ratePct(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)} %`;
}

/** 件数。**null（測っていない）は 0 と書かない。** */
export function countOrDash(value: number | null): string {
  return value === null ? '—' : formatCount(value);
}

export function CampaignDetailPage(): React.ReactElement {
  const { campaignId } = routeApi.useParams();
  const navigate = useNavigate();
  const canHalt = usePermission(OPS_PERMISSION.bulkHalt);
  const [haltOpen, setHaltOpen] = React.useState(false);

  const query = useQuery({
    queryKey: qk.opsCampaign(campaignId),
    queryFn: () => api.get<OpsCampaignDetailView>(`/campaigns/${campaignId}`),
    // 停止直後は「本当に止まったか」を見に来る画面なので短めに引き直す。
    refetchInterval: 5_000,
  });

  // `GET /ops/campaigns/:id` は `ops.bulk.halt` で守られている。
  // 権限が無い者には 403 の画面ではなく理由を出す。
  if (!canHalt) return <PermissionDenied />;

  const campaign = query.data;
  const batches: BatchRow[] = Object.entries(campaign?.batches ?? {})
    .map(([state, count]) => ({ state, count }))
    .sort((a, b) => b.count - a.count);
  const live = batches.filter((b) => LIVE_BATCH_STATES.includes(b.state));
  const liveTotal = live.reduce((s, b) => s + b.count, 0);

  const columns: Array<DataTableColumn<BatchRow>> = [
    {
      id: 'state',
      header: 'バッチの状態',
      cell: (b) => (
        <Badge variant={LIVE_BATCH_STATES.includes(b.state) ? 'warning' : 'muted'} size="sm">
          {labelOf(b.state)}
        </Badge>
      ),
    },
    { id: 'count', header: '件数', align: 'right', cell: (b) => formatCount(b.count) },
  ];

  return (
    <>
      <PageHeader
        title={campaign?.name ?? '一斉配信'}
        description={campaign ? `${campaign.organizationName} / ${campaign.subject}` : undefined}
        breadcrumbs={[{ label: '一斉配信', href: '/campaigns' }, { label: campaign?.name ?? '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {campaign ? <StatusBadge status={campaign.status} dot pulse={campaign.status === 'sending'} /> : null}
            {campaign && HALTABLE.has(campaign.status) ? (
              <Button variant="destructive" size="sm" onClick={() => setHaltOpen(true)}>
                <OctagonX aria-hidden />
                緊急停止
              </Button>
            ) : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {campaign ? (
        <div className="space-y-6">
          {/*
            **停止したのに払い出されうるバッチが残っている状態を最優先で見せる。**
            ここが 0 になって初めて「止まった」と言える。
          */}
          {campaign.status === 'halted' && liveTotal > 0 ? (
            <Alert variant="destructive">
              <AlertTitle>停止済みですが、まだ {formatCount(liveTotal)} バッチが残っています</AlertTitle>
              <AlertDescription>
                {live.map((b) => `${labelOf(b.state)} ${b.count}`).join(' / ')}。
                ノードが払い出し済みのバッチは、報告が返るまでこの表に残ります。
                数分待っても減らない場合は、ノードの <code className="font-mono">mailsv-bulkinject</code> と
                エージェントの疎通を確認してください。
              </AlertDescription>
            </Alert>
          ) : null}
          {campaign.status === 'halted' && liveTotal === 0 ? (
            <Alert variant="success">
              <AlertTitle>停止が反映されています</AlertTitle>
              <AlertDescription>
                払い出し待ち・払い出し済みのバッチは残っていません。既に送信された分は取り消せません。
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="宛先" value={formatCount(campaign.recipientCount)} unit="件" />
            <StatCard label="送信済み" value={formatCount(campaign.sent)} unit="通" />
            <StatCard
              label="注入失敗"
              value={formatCount(campaign.failed)}
              unit="通"
              tone={campaign.failed > 0 ? 'destructive' : 'default'}
              hint="相手のサーバーへ渡る前に落ちた通数。原因はこちら側にあります。"
            />
            <StatCard
              label="苦情率"
              value={`${(campaign.complaintRateBp / 100).toFixed(2)} %`}
              tone={campaign.complaintRateBp >= 30 ? 'destructive' : campaign.complaintRateBp >= 10 ? 'warning' : 'default'}
              hint="0.3%（30bp）が Gmail / Yahoo! の警戒線です。"
            />
          </div>

          <Section title="配信の状態">
            <div className="rounded-lg border border-border bg-card p-4">
              <KeyValue
                columns={3}
                items={[
                  {
                    key: '契約組織',
                    value: (
                      <Link
                        to="/organizations/$orgId"
                        params={{ orgId: campaign.organizationId }}
                        search={{ tab: 'overview' }}
                        className="hover:text-primary"
                      >
                        {campaign.organizationName}
                      </Link>
                    ),
                  },
                  { key: '件名', value: campaign.subject },
                  { key: '送信元ストリーム', value: campaign.sendingStreamId ?? '—' },
                  { key: '予約', value: formatDateTime(campaign.scheduledAt) },
                  { key: '解放', value: formatDateTime(campaign.releasedAt) },
                  { key: '開始', value: formatDateTime(campaign.startedAt) },
                  { key: '完了', value: formatDateTime(campaign.completedAt) },
                  { key: '取り消し', value: formatDateTime(campaign.cancelledAt) },
                  { key: '作成', value: formatDateTime(campaign.createdAt) },
                ]}
              />
            </div>
          </Section>

          <Section
            title="バッチの状態"
            description="ノードへ払い出す単位。停止が効いたかどうかは、ここに払い出し待ち・払い出し済みが残っていないかで判断します。"
          >
            <DataTable
              columns={columns}
              rows={batches}
              rowKey={(b) => b.state}
              loading={query.isLoading}
              emptyTitle="バッチがまだ積まれていません"
              emptyDescription="宛先の確定（validated）より前の配信では空になります。"
            />
          </Section>

          <Section title="到達の内訳">
            <div className="rounded-lg border border-border bg-card p-4">
              <KeyValue
                columns={3}
                items={[
                  { key: '配達', value: formatCount(campaign.delivered) },
                  { key: '恒久バウンス', value: formatCount(campaign.hardBounced) },
                  { key: '一時バウンス', value: formatCount(campaign.softBounced) },
                  { key: '苦情', value: formatCount(campaign.complained) },
                  { key: '配信停止', value: formatCount(campaign.unsubscribed) },
                  { key: 'バウンス率', value: `${(campaign.bounceRateBp / 100).toFixed(2)} %` },
                ]}
              />
            </div>
          </Section>

          <MeasureSection campaign={campaign} />

          <HaltDialog
            campaign={campaign}
            open={haltOpen}
            onOpenChange={setHaltOpen}
            onHalted={() => void navigate({ to: '/campaigns/$campaignId', params: { campaignId } })}
          />
        </div>
      ) : null}
    </>
  );
}

/* ----------------------------------------------------------- 効果測定 */

/**
 * クリックの内訳と返信。
 *
 * **サポートで「配信の効果が見たい」と言われたときに即答するための面。**
 * これまで運営が数字を出すには契約者へ代理ログインし直すしかなかった。
 * 出典は契約者面・統合面と同じサービス（`campaign-measure.ts`）なので、
 * ここと契約者の画面で数字が食い違うことは無い。
 *
 * **「測っていない」を 0 と書かない。** リストの `track_clicks` が false の
 * 配信では数字を出さず、理由を出す。0 と書くと「押されなかった」と読まれ、
 * 「計測を有効にしましょう」という一番大事な案内が出せなくなる。
 */
function MeasureSection({ campaign }: { campaign: OpsCampaignDetailView }): React.ReactElement {
  const { clicks, replies } = campaign;

  const linkColumns: Array<DataTableColumn<OpsCampaignClickLink>> = [
    {
      id: 'url',
      header: 'リンク',
      cell: (l) =>
        l.url ? (
          // 運営の画面から契約者のリンクへ実際に飛ばない（フィッシングの
          // 中継になりうる）。**文字列として出すだけ**にする。
          <span className="break-all font-mono text-xs">{l.url}</span>
        ) : (
          <span className="text-muted-foreground">
            不明なリンク（本文を作り直して宛先表が入れ替わった分）
          </span>
        ),
    },
    { id: 'clicks', header: '延べ', align: 'right', cell: (l) => formatCount(l.clicks) },
    { id: 'unique', header: 'ユニーク', align: 'right', cell: (l) => formatCount(l.uniqueClicks) },
    {
      id: 'share',
      header: '構成比',
      align: 'right',
      cell: (l) => (l.share === null ? '—' : `${(l.share * 100).toFixed(1)} %`),
    },
  ];

  return (
    <Section
      title="効果測定"
      description="率の分母は配信成功数（delivered）です。宛先数ではありません。"
    >
      <div className="space-y-3">
        {!clicks.tracked ? (
          <Alert variant="default">
            <AlertTitle>この配信ではクリックを計測していません</AlertTitle>
            <AlertDescription>
              リストの「クリックを計測する」が無効です。数字が 0 なのではなく、記録していません。
              契約者が計測を有効にすると、次回の配信からリンク別の内訳が出ます。
            </AlertDescription>
          </Alert>
        ) : null}
        {clicks.source === 'stats' ? (
          <Alert variant="warning">
            <AlertTitle>生ログの保持期間（{clicks.retentionDays} 日）を過ぎています</AlertTitle>
            <AlertDescription>
              総数だけが残っており、リンク別・時系列の内訳はもう出せません。
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="クリック（延べ）" value={countOrDash(clicks.total)} unit="回" />
          <StatCard label="クリック（ユニーク）" value={countOrDash(clicks.unique)} unit="人" />
          <StatCard
            label="クリック率（ユニーク）"
            value={ratePct(clicks.uniqueRate)}
            hint={`分母は配信成功数 ${formatCount(clicks.denominator)} 通（${clicks.denominatorBasis}）。`}
          />
          <StatCard
            label="返信"
            value={countOrDash(replies.total)}
            unit={replies.available ? '通' : undefined}
            hint={
              replies.available
                ? '受信箱に届いた返信の数です。'
                : '返信をコントロールプレーンへ取り込む経路がまだありません。0 件という意味ではありません。'
            }
          />
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <KeyValue
            columns={3}
            items={[
              { key: '最初のクリック', value: formatDateTime(clicks.firstAt) },
              { key: '最後のクリック', value: formatDateTime(clicks.lastAt) },
              { key: '集計の出典', value: clicks.source === 'events' ? '生ログ（実測）' : '焼き付け値' },
            ]}
          />
        </div>

        {clicks.tracked ? (
          <>
            <DataTable
              columns={linkColumns}
              rows={clicks.links}
              rowKey={(l) => String(l.linkIndex ?? 'unknown')}
              emptyTitle="リンクがありません"
              emptyDescription="本文に外部リンクが無いか、まだ本文が組み立てられていません。"
            />
            {clicks.linksTruncated ? (
              <p className="text-xs text-muted-foreground">
                リンクが多いため上位のみ表示しています。全件は契約者パネルの CSV 書き出しで取得できます。
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </Section>
  );
}

/* ----------------------------------------------------------- 緊急停止 */

function HaltDialog({
  campaign,
  open,
  onOpenChange,
  onHalted,
}: {
  campaign: OpsCampaignDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHalted: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const halt = useMutation({
    mutationFn: () => api.post(`/campaigns/${campaign.id}/halt`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'campaigns'] });
      onOpenChange(false);
      onHalted();
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="この配信を緊急停止する"
      description={`${campaign.organizationName} / ${campaign.name}（未送信 ${formatCount(
        campaign.recipientCount - campaign.sent,
      )} 件）`}
      submitLabel="停止する"
      pending={halt.isPending}
      error={halt.error}
      // サーバー側の zod は 5 文字以上。先に止めて 400 を出さない。
      disabled={reason.trim().length < 5}
      onSubmit={() => halt.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          停止すると未払い出しの宛先は抑止され、<strong>再開できません</strong>。
          <strong>既にノードへ払い出したバッチは止まりません</strong>（取り消しをノードへ伝える経路が無く、
          ノードは持っていった分を注入し終えてから停止します）。
          停止後はこの画面のバッチ表で、払い出し待ち・払い出し済みが残っていないかを必ず確認してください。
          理由は監査ログに残り、契約者へも通知されます。
        </AlertDescription>
      </Alert>

      <Field label="停止の理由" required hint="5 文字以上。契約者への通知本文に載ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="苦情率が 0.5% を超えたため（同一ノードの他契約者への影響を回避）"
        />
      </Field>
    </FormDialog>
  );
}
